import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'

/**
 * Night Tube analytics — three events, identical schema across iOS / Android
 * / Web (cross-platform contract, decided in the night-tube brief). Web
 * routes them through the [MetroAnalyticsReporter] sink; `ModesToggle`
 * injects `realMetroAnalyticsReporter`, which forwards to GA4 + Amplitude
 * in production (no-op outside production / before consent).
 */

/** Lowercase weekday name as emitted on the wire. Mirrors iOS V1
 *  `MetroMapAnalytics.swift:67-89` `day_of_week` payload (full lowercase,
 *  not abbreviated). The brief originally specified 3-letter abbreviations,
 *  but iOS V1 is already shipping `"sunday"..."saturday"` to production and
 *  the data warehouse schema expects that shape — see lead decision 2026-06-01. */
export type DayOfWeek =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'

/**
 * Cross-platform `source` contract for `night_mode_enabled`. iOS V1
 * (`MetroMap.swift:195`) emits a free-string `source`; mobile uses
 * `'premium_user'`, `'paywall'`, `'deeplink'`. Web is paywall-free
 * (decision #4), so the only path is `'web_user'` — a distinct value
 * lets the analytics dashboard separate the web cohort from paywall-gated
 * mobile cohorts in cross-platform comparisons.
 *
 * The wire payload is a plain string, NOT a JSON-encoded enum; adding a
 * new value on the mobile side does NOT require a web schema bump.
 */
export type NightModeSource = 'web_user' | 'premium_user' | 'paywall' | 'deeplink'

/**
 * Format a `Date` as `"HH:mm"` 24-hour string (e.g. `"02:35"`, `"19:04"`).
 * Mirrors iOS V1 `MetroMapAnalytics.swift:67-89` `time_of_day` payload
 * (raw wall-clock time, NOT a bucket). Dashboard-side aggregation buckets
 * this back to morning/afternoon/evening/night when needed — zero-loss on
 * the wire.
 *
 * Uses local (browser) time, matching iOS / Android client-time semantics
 * for the same event.
 */
export function formatHHmm(now: Date = new Date()): string {
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

/**
 * Format a `Date` as a lowercase full weekday name (`"sunday"..."saturday"`).
 * Mirrors iOS V1 `MetroMapAnalytics.swift:67-89` `day_of_week` payload.
 * `Date.getDay()` returns 0=Sunday..6=Saturday, matching `Calendar.weekday - 1`
 * on iOS.
 */
export function formatFullDay(now: Date = new Date()): DayOfWeek {
  const days: readonly DayOfWeek[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ]
  return days[now.getDay()]!
}

/**
 * Web has no premium gate for night-mode (decision #4 — `// TODO(night-tube):
 * premium-gate-deferred — web paywall policy at project_metro_web_no_paywall`).
 * Send `is_premium: false` so the cross-platform analytics schema stays
 * uniform; iOS/Android emit the user's real premium state. When web wires
 * Adapty in the future, replace the literal with a real lookup.
 */
const WEB_IS_PREMIUM = false

/**
 * Encode the analytics-layer mode string. iOS V1
 * (`MetroMapAnalytics.swift:53-89`) emits `"day"` for normal mode and
 * `"night"` for night mode — this is INTENTIONALLY decoupled from the
 * schema-level `Mode` enum (`'normal' | 'night' | 'unknown'`). The
 * analytics layer never sees `'unknown'` (it only happens at decode time
 * for forward-compat with future schema values) and the legacy `"day"`
 * string is what the production data warehouse already accepts. See lead
 * decision 2026-06-01 — Option A (match iOS V1 prod verbatim).
 */
function encodeAnalyticsMode(isNight: boolean): 'day' | 'night' {
  return isNight ? 'night' : 'day'
}

export function reportNightModeToggleAttempted(
  reporter: MetroAnalyticsReporter,
  params: { currentIsNight: boolean; targetIsNight: boolean },
): void {
  reporter('night_mode_toggle_attempted', {
    current_mode: encodeAnalyticsMode(params.currentIsNight),
    target_mode: encodeAnalyticsMode(params.targetIsNight),
    is_premium: WEB_IS_PREMIUM,
  })
}

export function reportNightModeEnabled(
  reporter: MetroAnalyticsReporter,
  params: { source: NightModeSource; now?: Date } = { source: 'web_user' },
): void {
  reporter('night_mode_enabled', {
    source: params.source,
    time_of_day: formatHHmm(params.now),
    day_of_week: formatFullDay(params.now),
  })
}

export function reportNightModeDisabled(
  reporter: MetroAnalyticsReporter,
  params: { now?: Date } = {},
): void {
  reporter('night_mode_disabled', {
    time_of_day: formatHHmm(params.now),
    day_of_week: formatFullDay(params.now),
  })
}
